/* eslint-disable camelcase, no-param-reassign */
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import datadog from '@nerdwallet/datadog';
import appConfig from '@nerdwallet/app-config';

import { NOTFOUND_ERROR_MSG, OverrideError } from './errors';
import getErrorHandlers from './error-handling';
import verticalInformation from '../config/vertical-definitions';
import fetchFromMarketplace from './marketplace-sdk';
import {
  getStaticAffiliateData,
  getStaticNetworkLinkData,
  getStaticRedirectData,
} from './static-data-sdk';
import { decodeUrl, validateOverrideUrl } from './override-url-utils';
import modifySharedId from './modify-shared-id-util';
import getTermsFeesLink from './get-terms-fees-link';
import {
  isFormattedForPartnerTrackingInjection,
  injectPartnerTrackingId,
  updateQueryParams,
  queryParamExists,
} from './partner-tracking-utils';
import getAlternateRedirects from './alternate-redirects';

// App-config flags for using Marketplace redirect details
const { CATEGORIES_ENABLED } = appConfig.DIRECT_MARKETPLACE_FETCH;

/**
 * Helper function to determine if we should fetch directly from Marketplace
 */
const shouldFetchMarketplaceDetails = (category) => {
  // CATEGORIES_ENABLED list items match the keys of the verticalInformation map
  if (!_.includes(CATEGORIES_ENABLED, category)) {
    return false;
  }
  return true;
};

/**
 * Helper function to create a destination url
 */
const makeDestinationUrl = (url, partnerParams) => {
  if (!partnerParams) {
    return url;
  }
  // Append partner params to url
  try {
    partnerParams = decodeURIComponent(partnerParams);
  } catch (e) {
    // Catch malformed URI errors
    if (e.name !== 'URIError') {
      throw e;
    }
  }
  return updateQueryParams(url, partnerParams);
};

/**
 * Helper function to preserve vertical name from Redirect Service (e.g. vertical column from network links table)
 * Used in container.jsx for GTM tags
 * https://github.com/NerdWallet/interstitial-client/blob/master/client/routes/container.jsx#L40
 * https://nerdwallet.atlassian.net/wiki/spaces/ENG/pages/121218111/Google+Tag+Manager+GTM#GoogleTagManager(GTM)-Variablescurrentlyexposed
 */
const getLegacyVerticalName = (productTypeName) => {
  switch (productTypeName) {
    case 'auto_loans':
      return 'Auto Loans';
    case 'banking':
      return 'Banking';
    case 'education_loans':
      return 'Student Loans';
    case 'first_time_home_buyer_programs':
      return 'Mortgages';
    case 'investing':
      return 'Investing';
    case 'mortgages':
      return 'Mortgages';
    case 'personal_loans':
      return 'Personal Loans';
    /**
     * TODO: Set credit_cards once redirect service is deprecated.
     * Sets credit_cards legacy to null as redirect service does not
     * return a product_type_name for credit cards
     */
    case 'credit_cards':
      return null;
    default:
      return productTypeName;
  }
};

export const getDestinationWithPartnerTrackingPrefixPath = (
  destinationUrl,
  partnerTrackingPath
) => {
  const url = new URL(destinationUrl);
  const parts = url.href.split(url.pathname);
  const separator = url.pathname.slice(-1) === '/' ? '' : '/';
  return `${parts[0]}${url.pathname}${separator}${partnerTrackingPath}${parts[1]}`;
};

/**
 * MAIN FUNCTION
 */
export default async ({ store, res, req, match }) => {
  // Create error handlers
  const { handleInvalidOverride, handleNotFound } = getErrorHandlers({
    req,
    res,
    store,
    service: 'interstitial-client',
  });

  const routeVerticalSlug = match.params.slug;
  const vertical = verticalInformation[routeVerticalSlug];
  if (!vertical) {
    handleNotFound({ product: match.params });
    throw new Error(NOTFOUND_ERROR_MSG);
  }

  const { id } = match.params;
  const { category } = vertical;
  // "override" is a unique link that must be base64 encoded.
  // This link is used when we want to redirect to a unique url returned by the partner.
  // Since the link is not stored on our end, the redirect service should check
  // the decoded override link against a whitelist.
  const override = _.get(req, 'query.override');
  const basicClientData = {
    quantcastLabel: vertical.quantcastLabel,
    gtmEvent: vertical.gtmEvent,
    productId: id,
    referPath: req.headers.referer,
  };

  const basicLoggerData = {
    category,
    verticalEventFields: vertical.eventFields,
    productId: id,
    verticalProductType: vertical.productType,
  };

  /**
   * Helper function to create clientData and loggerData from Redirect Service api response
   */
  const extractFromRedirectResponse = (product) => {
    const partnerParams = _.get(req, 'query.partnerParams');
    const destinationUrl = makeDestinationUrl(product.url, partnerParams);

    const clientData = {
      ...basicClientData,
      destinationUrl,
      monetizing: product.monetizing,
      uniqueClickId: product.unique_click_id,
      vertical: product.vertical,
      tradeName: product.trade_name,
      secName: product.sec_legal_name,
      advLink: product.sec_form_adv_url,
      productType: product.product_type,
      marketplaceOfferProductType: product?.marketplace_offer?.product_type,
      slug: product.slug,
    };

    const loggerData = {
      ...basicLoggerData,
      productSlug: product.slug,
      monetizing: product.monetizing,
      sponsored: product.sponsored,
      affiliateId: product.network,
      additionalQueryParams: product.query_params,
      uniqueClickId: product.unique_click_id,
      institutionName: product.trade_name,
      productName: product.product_name,
      productType: product.product_type,
      offerName: product.offer_name,
      offerAccountType: product.offer_account_type,
      offerReviewType: product.offer_review_type,
      product,
    };

    // Special case for investing-products to pull in related
    // products for non-monetizing advisory products
    // as well as handle any special disclaimers
    if (vertical.specialDisclaimers) {
      const specialDisclaimer = vertical.specialDisclaimers[product.slug];
      if (specialDisclaimer) {
        clientData.disclaimer = specialDisclaimer;
      }
    }

    // Set other options for non-monetizing investing products
    if (
      !clientData.monetizing &&
      _.get(req, 'prefetchData.investingAlternatives')
    ) {
      clientData.otherOptions = _.get(
        req,
        'prefetchData.investingAlternatives'
      );
      clientData.showOtherOptions = true;
    }

    return {
      clientData,
      loggerData,
    };
  };

  /**
   * Helper function to transform a Marketplace response to Redirect Service response
   */
  const transformToRedirectServiceProduct = (marketplaceProduct = {}) => {
    /**
     * ==== Create a url for redirecting to a partner site ====
     */
    const clickId =
      _.get(req, 'query.shopping_finish_id') ||
      _.get(req, 'query.finish_id') ||
      uuidv4().replace(/-/g, '');

    let clickIdPrefix = null;
    let partnerTrackingPrefixWithinUrl = null;
    const {
      affiliate_id,
      destination_url,
      partner_tracking_parameter,
      product_type_name: marketplaceProductType,
    } = marketplaceProduct;

    let destinationUrl = destination_url;

    /**
     * Use terms_and_fees_link as destination if specified and it exists
     *
     * TODO: https://nerdwallet.atlassian.net/browse/PP-3784
     *       Port this logic over to fetch-uuid-redirect-data.js once
     *       uuid support is added for cc proxy class in marketplace.
     */
    const termsAndFeesLink = getTermsFeesLink(req, marketplaceProduct);
    if (termsAndFeesLink) {
      destinationUrl = termsAndFeesLink;
    }

    let partnerTrackingParam = partner_tracking_parameter;
    // -9999 is consistent with redirect.nerdwallet response and may be required by DWH
    let network = affiliate_id !== null ? affiliate_id : -9999;
    let isMonetizable = marketplaceProduct.monetizable;

    const alternateRedirect = getAlternateRedirects(req, marketplaceProduct);
    if (alternateRedirect) {
      destinationUrl = alternateRedirect.destinationUrl;
      partnerTrackingParam = alternateRedirect.partnerTrackingParam;
      isMonetizable = alternateRedirect.monetizable;
    }

    // "override" is a unique link from query params that is base64 encoded
    // If it passes validation, it replaces the originally stored destinationUrl
    if (override) {
      try {
        let decodedOverrideUrl = null;
        decodedOverrideUrl = decodeUrl(marketplaceProductType, override);
        const whiteList = _.get(
          marketplaceProduct,
          'supplemental_data.override_whitelist',
          []
        );
        if (
          validateOverrideUrl(
            marketplaceProductType,
            decodedOverrideUrl,
            whiteList
          )
        ) {
          destinationUrl = decodedOverrideUrl;
          req.log.info({
            context: { destinationUrl, product_type: marketplaceProductType },
            message: 'URI overridden by override parameter.',
          });
        }
      } catch (e) {
        if (e instanceof OverrideError) {
          handleInvalidOverride({
            product: {
              category,
              id,
              override,
              productType: marketplaceProductType,
            },
            error: e.message,
          });
        }
        throw e;
      }
    }

    if (destinationUrl) {
      let trackingId = clickId;

      // IF formatted destination url (eg: 'www.test-issuer.com?sid=NW_IC_CLICK_ID')
      if (isFormattedForPartnerTrackingInjection(destinationUrl)) {
        destinationUrl = injectPartnerTrackingId(destinationUrl, trackingId);
      } else {
        // If no partner tracking param, find it in affiliate_networks.json
        if (!partnerTrackingParam) {
          const affiliateNetwork = getStaticAffiliateData(
            destinationUrl,
            affiliate_id,
            req
          );

          if (affiliateNetwork) {
            partnerTrackingParam = affiliateNetwork.partnerTrackingParam;
            network = affiliateNetwork.id;
            clickIdPrefix = affiliateNetwork.clickIdPrefix;
            partnerTrackingPrefixWithinUrl = affiliateNetwork.trackingUrlPrefix;
          }
        }

        if (clickIdPrefix) {
          trackingId = clickIdPrefix + trackingId;
        }

        if (partnerTrackingPrefixWithinUrl) {
          // Append partner url prefix to destination url pathname (e.g /foobar:a1b2c3)
          destinationUrl = getDestinationWithPartnerTrackingPrefixPath(
            destinationUrl,
            `${partnerTrackingPrefixWithinUrl}${trackingId}`
          );
        }

        if (
          partnerTrackingParam &&
          !queryParamExists(destinationUrl, partnerTrackingParam)
        ) {
          // Append partner tracking param and clickId if present (e.g. subid=<clickId>)
          // Important because this is how NW monetizing partners record user clicks
          destinationUrl = updateQueryParams(
            destinationUrl,
            `${partnerTrackingParam}=${trackingId}`
          );
        }
      }

      /**
       * Modify Discover Credit Cards sharedid query param on the destination url
       * for apply now links coming from the nerdwallet mobile app, or for links
       * coming from special pages defined in modify-shared-id-utils.js.
       *
       * TODO: https://nerdwallet.atlassian.net/browse/PP-3784
       *       Port this logic over to fetch-uuid-redirect-data.js once
       *       uuid support is added for cc proxy class in marketplace.
       */
      const originalDestinationUrl = destinationUrl;
      destinationUrl = modifySharedId(
        req,
        marketplaceProductType,
        destinationUrl
      );

      // log if it was changed.
      if (destinationUrl !== originalDestinationUrl) {
        req.log.info({
          message: 'Set Discover mobile sharedid',
          context: {
            product_type: marketplaceProductType,
            original_destination_url: originalDestinationUrl,
            modified_url: destinationUrl,
          },
        });
      }
    }

    /**
     * ==== Get value for monetizing and sponsored ====
     */
    if (!_.includes(['banking', 'investing'], marketplaceProductType)) {
      // Preserves Redirect Service behavior
      isMonetizable = isMonetizable ? 1 : 0;
    }

    /**
     * ==== Get value for marketplace_offer ====
     */
    const offer = marketplaceProduct.offer || null; // TODO: remove null check once Marketplace also returns IPO in get_legacy_id endpoint
    if (offer !== null) {
      const product = marketplaceProduct.product || {};
      const institution = marketplaceProduct.institution || {};
      offer.product = product;
      offer.product.institution = institution;
    }

    /**
     * ==== Get additional values ====
     */
    const supplementalData = marketplaceProduct.supplemental_data || {};

    // Hardcode in IC. TODO: handle for future verticals (e.g. credit cards)
    const query_params = _.get(vertical, 'queryParams', []);

    return {
      monetizing: isMonetizable,
      network,
      slug: marketplaceProduct.name,
      sponsored: isMonetizable,
      unique_click_id: clickId,
      url: destinationUrl,
      vertical: getLegacyVerticalName(marketplaceProductType),
      query_params,
      redirect_page_customizations: { custom_html_content: '' },
      // Product-type specific data
      marketplace_offer: offer,
      offer_account_type: _.get(supplementalData, 'offer_account_type', null),
      offer_name: _.get(supplementalData, 'offer_name', null),
      offer_review_type: _.get(supplementalData, 'offer_review_type', null),
      product_name: _.get(supplementalData, 'product_name', null),
      product_type: _.get(supplementalData, 'product_type', null),
      sec_legal_name: _.get(supplementalData, 'sec_legal_name', null),
      sec_form_adv_url: _.get(supplementalData, 'sec_form_adv_url', null),
      trade_name: _.get(supplementalData, 'trade_name', null),
    };
  };

  /**
   * Fetch from Marketplace
   */
  if (shouldFetchMarketplaceDetails(routeVerticalSlug)) {
    req.log.info('Fetching redirect details from Marketplace');

    const response = await fetchFromMarketplace({
      category,
      id,
      routeVerticalSlug,
      req,
      res,
      store,
    });

    let finalProduct;
    const productType = (response || {}).product_type_name;

    datadog.increment(
      'interstitial_client.fetch_redirect_details.marketplace.success',
      1,
      { tags: { redirect_type: 'legacy', product_type: productType } }
    );

    if (response) {
      req.log.info({
        message: `Using redirect details from Marketplace for product type ${productType} from IC category ${routeVerticalSlug}`,
        context: { product_type: vertical?.product_type },
      });
      finalProduct = transformToRedirectServiceProduct(response);
    } else {
      // Otherwise, fallback to static network links data
      req.log.warn({
        message: `No redirect details found from Marketplace for IC category ${routeVerticalSlug}`,
        context: { product_type: productType },
      });
      if (routeVerticalSlug === 'network-links') {
        finalProduct = getStaticNetworkLinkData(id, req);
        if (!finalProduct) {
          handleNotFound({
            product: {
              category,
              id,
              override,
              productType: vertical?.productType,
            },
          });
          throw new Error(NOTFOUND_ERROR_MSG);
        }
        req.log.info({
          message: 'Using redirect details from static network-links',
          context: {
            id,
            affiliateId: finalProduct.network,
            destinationUrl: finalProduct.url,
            product_type: finalProduct.vertical,
            monetizing: finalProduct.monetizing,
            url: req.url,
            referrer: _.get(req, 'headers.referrer'),
          },
        });
      } else {
        handleNotFound({
          product: {
            category,
            id,
            override,
            productType: vertical?.productType,
          },
        });
        throw new Error(NOTFOUND_ERROR_MSG);
      }
    }

    const { clientData, loggerData } =
      extractFromRedirectResponse(finalProduct);
    return { clientData, loggerData };
  }

  /**
   * check if we have any static redirect data for this route slug.
   */

  const staticData = getStaticRedirectData(routeVerticalSlug, id, req);

  if (staticData) {
    req.log.info({
      message: `Using static redirect details for IC category ${routeVerticalSlug} id ${id}`,
      context: { product_type: vertical?.productType },
    });

    const { clientData, loggerData } = extractFromRedirectResponse(staticData);
    return { clientData, loggerData };
  }

  /**
   * If we fall through to here, we don't have a redirect statically defined. So we have
   * exhausted all possible options and should throw a compass (404) at the user.
   */

  req.log.info({
    routeVerticalSlug,
    id,
    message: `no-fallback-redirect-found for ${routeVerticalSlug} :: ${id}`,
    context: { product_type: vertical?.productType },
  });

  handleNotFound({
    product: {
      category,
      id,
      override,
      productType: vertical?.productType,
    },
  });

  throw new Error(NOTFOUND_ERROR_MSG);
};
